import React, { useState, useEffect } from "react";
import { Card, Table, Typography, message, Spin, Row, Col, Button } from "antd";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import clientBillingApi from "../store/api";
import FacturasTable from "../../../admin/facturas/components/FacturasTable";
import useResizeObserverErrorSuppression from "../../../../hooks/useResizeObserverErrorSuppression";
import request from "../../../../helper/request";
import profileApi from "../../../client/profile/store/api";

const { Title, Text } = Typography;

const BillingDashboard = () => {
  const history = useHistory();
  const authUser = useSelector((state) => state.auth.user);
  const [user, setUser] = useState(authUser);
  const client = user?.client;
  const [billingLogs, setBillingLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });

  // New state for payments due section
  const [paymentsDue, setPaymentsDue] = useState([]);
  const [paymentsDueLoading, setPaymentsDueLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Suppress ResizeObserver errors
  useResizeObserverErrorSuppression();

  const fetchPaymentsDue = async () => {
    try {
      setPaymentsDueLoading(true);
      // Fetch all facturas with only status filter - NO date filter
      const params = {
        page: 1,
        pageSize: 1000, // Get all outstanding invoices regardless of date
        status: "overdue,partial,unpaid" // Only get outstanding invoices
      };
      console.log(
        "fetchPaymentsDue - calling API with params:",
        params,
        "at",
        new Date().toISOString()
      );
      const response = await clientBillingApi.getFacturas(params);
      console.log(
        "fetchPaymentsDue - received response:",
        response?.data?.length,
        "facturas"
      );

      const facturas = response.data || [];
      console.log("fetchPaymentsDue - received facturas:", facturas.length);

      // Sort: overdue first, then partial, then unpaid
      const sortedData = facturas.sort((a, b) => {
        const statusOrder = { overdue: 0, partial: 1, unpaid: 2 };
        return (statusOrder[a.status] || 3) - (statusOrder[b.status] || 3);
      });
      console.log("fetchPaymentsDue - final sorted data:", sortedData.length, "facturas");

      setPaymentsDue(sortedData);
    } catch (error) {
      console.error("Error fetching payments due:", error);
      message.error("Failed to load payments due");
      setPaymentsDue([]);
    } finally {
      setPaymentsDueLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      setUserLoading(true);
      const response = await profileApi.getProfile();
      setUser(response);
      console.log(
        "Updated user profile with client:",
        response?.client?.facturaPaymentTerms
      );
    } catch (error) {
      console.error("Error fetching user profile:", error);
      // Fallback to auth user if API fails
      setUser(authUser);
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    console.log("BillingDashboard - useEffect triggered, mounting dashboard");
    fetchUserProfile();
    fetchBillingLogs();
    fetchPaymentsDue();
  }, []);

  const fetchBillingLogs = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      console.log("Fetching billing logs...", { page, pageSize });
      const response = await clientBillingApi.getBillingLogs({ page, pageSize });
      console.log("Billing logs response:", response);

      // Handle both old format (direct array) and new format (with data/meta)
      if (response.data && response.meta) {
        // New paginated format
        setBillingLogs(response.data);
        setPagination({
          current: response.meta.currentPage,
          pageSize: response.meta.itemsPerPage,
          total: response.meta.totalItems
        });
      } else {
        // Fallback to old format
        const logs = response.data || response || [];
        setBillingLogs(logs);
        setPagination((prev) => ({ ...prev, total: logs.length }));
      }
    } catch (error) {
      message.error("Failed to load billing logs");
      console.error("Error fetching billing logs:", error);
      setBillingLogs([]); // Set empty array on error
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    console.log("handlePageChange called with:", { page, pageSize });
    fetchBillingLogs(page, pageSize);
  };

  const handleViewFactura = (facturaId) => {
    const factura = paymentsDue.find((f) => f.id === facturaId);
    history.push(`/billing/facturas/${facturaId}`, { factura });
  };

  const handleViewAllFacturas = () => {
    history.push("/billing/facturas");
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleManageInStripe = async () => {
    // Show browser confirmation dialog
    const confirmed = window.confirm(
      "You will be redirected to Stripe to manage your payment methods.\n\nPlease note: It may take up to 2 minutes for Stripe to show all your subscriptions and payment methods.\n\nDo you want to continue?"
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await request.post("/payment-method/create-portal-session");
      // Open Stripe portal in new tab
      window.open(response.url, "_blank");
      message.success("Redirected to Stripe portal");
    } catch (error) {
      console.error("Error creating Stripe portal session:", error);
      message.error("Failed to open Stripe portal");
    }
  };

  const formatEventType = (eventType) => {
    const eventMap = {
      "payment-method-add": "Payment Method Added",
      "payment-method-delete": "Payment Method Deleted",
      "payment-method-default-set": "Default Payment Method Changed",
      "payment-method-default-auto": "Default Payment Method Auto-Assigned",
      "payment-method-stripe-portal": "Stripe Portal Accessed",
      "manual-create": "Invoice Created",
      "manual-delete": "Invoice Deleted",
      "manual-note": "Note Added"
    };
    return eventMap[eventType] || eventType;
  };

  const formatFacturaPaymentTerms = (terms) => {
    const termsMap = {
      [-1]: "Manual Bill Only",
      [0]: "Pre-pay Required",
      [1]: "Daily",
      [7]: "Weekly",
      [30]: "Monthly"
    };
    return termsMap[terms] || "Daily";
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp) => format(new Date(timestamp), "MMM dd, yyyy HH:mm"),
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      width: 160,
      fixed: "left"
    },
    {
      title: "Event Type",
      dataIndex: "eventType",
      key: "eventType",
      render: formatEventType,
      width: 220
    },
    {
      title: "Description",
      dataIndex: "summary",
      key: "summary",
      ellipsis: true,
      width: 300
    },
    {
      title: "User",
      dataIndex: ["user", "name"],
      key: "user",
      render: (name, record) => name || record.user?.email || "System",
      width: 150
    },
    {
      title: "Invoice",
      dataIndex: ["factura", "facturaId"],
      key: "facturaId",
      render: (facturaId) => (facturaId ? <Text code>{facturaId}</Text> : "-"),
      width: 120
    }
  ];

  return (
    <div className="billing-dashboard-page client">
      <div className="content-wrapper">
        <div className="billing-dashboard">
          <Card>
            <Title level={3}>Billing Dashboard</Title>
            <p style={{ color: "#666", marginBottom: "24px" }}>
              Monitor your billing activity, payment methods, and invoice history.
            </p>
          </Card>

          {/* New sections row - 60% / 40% split */}
          <Row gutter={[24, 24]} style={{ marginTop: "24px", height: "45vh" }}>
            {/* Payments Due Section - 60% width */}
            <Col span={14}>
              <Card style={{ height: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px"
                  }}
                >
                  <Title level={4} style={{ margin: 0 }}>
                    Payments Due
                  </Title>
                  <Button type="link" onClick={handleViewAllFacturas}>
                    See more in Facturas
                  </Button>
                </div>
                <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
                  Outstanding invoices requiring payment
                </Text>

                <div
                  style={{
                    height: "calc(100% - 100px)",
                    overflow: "hidden",
                    position: "relative",
                    border: "1px solid #f0f0f0",
                    borderRadius: "6px"
                  }}
                >
                  <Spin spinning={paymentsDueLoading}>
                    <div style={{ position: "relative", height: "100%" }}>
                      <div className="payments-due-table">
                        <FacturasTable
                          dataSource={paymentsDue.slice(0, 4)}
                          pagination={null}
                          onRowClick={handleViewFactura}
                          loading={paymentsDueLoading}
                          onRefresh={fetchPaymentsDue}
                          sortConfig={sortConfig}
                          onSort={handleSort}
                          readOnly={true}
                          isClientSide={true}
                        />
                      </div>
                      <style>
                        {`.payments-due-table .ant-pagination { display: none !important; }`}
                      </style>
                      {paymentsDue.length > 4 && (
                        <>
                          {/* Fade overlay for the 5th row */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: "50px",
                              background:
                                "linear-gradient(to top, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0) 100%)",
                              pointerEvents: "none",
                              zIndex: 1
                            }}
                          />
                          {/* See Facturas button overlay */}
                          <div
                            style={{
                              position: "absolute",
                              bottom: "12px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              zIndex: 2
                            }}
                          >
                            <Button
                              type="primary"
                              size="small"
                              onClick={handleViewAllFacturas}
                              style={{
                                background: "#1890ff",
                                borderColor: "#1890ff",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                              }}
                            >
                              See more in Facturas
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                    {paymentsDue.length === 0 && !paymentsDueLoading && (
                      <div
                        style={{ textAlign: "center", padding: "40px", color: "#999" }}
                      >
                        No outstanding payments
                      </div>
                    )}
                  </Spin>
                </div>
              </Card>
            </Col>

            {/* Billing Settings Section - 40% width */}
            <Col span={10}>
              <Card style={{ height: "100%" }}>
                <Title level={4} style={{ marginBottom: "5px" }}>
                  Billing Settings
                </Title>
                <Spin spinning={userLoading}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                      <Text strong style={{ display: "block", marginBottom: "8px" }}>
                        Client Payment Terms
                      </Text>
                      <Text type="secondary">
                        {client?.facturaPaymentTerms
                          ? formatFacturaPaymentTerms(client.facturaPaymentTerms)
                          : "Daily"}
                      </Text>
                    </div>

                    <div
                      style={{ display: "flex", flexDirection: "column", gap: "12px" }}
                    >
                      <Button
                        type="default"
                        block
                        onClick={() => history.push("/billing/subscriptions")}
                      >
                        Subscriptions
                      </Button>

                      <Button
                        type="default"
                        block
                        onClick={() => history.push("/billing/payment-method")}
                      >
                        Payment Methods
                      </Button>

                      <Button
                        type="default"
                        block
                        onClick={() => history.push("/billing/upgrades")}
                      >
                        Upgrades
                      </Button>

                      <Button type="default" block onClick={handleManageInStripe}>
                        Manage Payment Info in Stripe ↗
                      </Button>
                      {/*
                      <Button
                        type="default"
                        block
                        onClick={() => history.push("/billing/invoicing")}
                      >
                        Old Invoices (deprecated)
                      </Button>
                      <Button
                        type="default"
                        block
                        onClick={() => history.push("/billing/pool-invoices")}
                      >
                        Old Pool Invoices (deprecated)
                      </Button>
                      */}
                    </div>
                  </div>
                </Spin>
              </Card>
            </Col>
          </Row>

          <Card style={{ marginTop: "24px" }}>
            <Title level={4}>Billing Log</Title>
            <Text type="secondary">
              Track all billing-related activities including payment methods, invoices,
              and system events.
            </Text>

            <div style={{ marginTop: "24px" }}>
              <Spin spinning={loading}>
                <div style={{ overflowX: "auto" }}>
                  <Table
                    columns={columns}
                    dataSource={billingLogs}
                    rowKey="id"
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} entries`,
                      onChange: handlePageChange,
                      onShowSizeChange: handlePageChange,
                      style: {
                        marginTop: "16px",
                        textAlign: "center"
                      },
                      size: "small"
                    }}
                    scroll={{ x: 950, y: 400 }}
                    size="middle"
                    locale={{
                      emptyText: loading
                        ? "Loading..."
                        : billingLogs.length === 0
                        ? "No billing activity yet"
                        : "No data"
                    }}
                  />
                </div>
              </Spin>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BillingDashboard;
